/**
 * User-safe mapping of backend reason codes to human-readable copy.
 * Unknown codes fall back to a generic explanation. Never expose
 * collection names, internal IDs, or stack traces.
 */
import type { OnboardingItem } from "./contracts";

const ITEM_TITLES: Record<string, string> = {
  tenant_profile_confirmed: "Tenant profile confirmed",
  company_configured: "At least one active company",
  branch_configured: "At least one active branch",
  owner_admin_exists: "An Owner or Admin exists",
  manager_trainer_exists: "A Manager or Trainer exists",
  representative_exists: "A Representative exists",
  template_selected: "CRM template selected",
  pipeline_stages_ready: "Pipeline stages ready",
  contact_import_ready: "Contact import ready",
  training_material_ready: "First-login training available",
};

export function titleForItem(key: string): string {
  return ITEM_TITLES[key] || "Setup item";
}

const REASONS: Record<string, string> = {
  // Tenant profile
  tenant_not_found: "The tenant record could not be located. Please contact support.",
  tenant_not_active: "The tenant is not marked active. Please contact support.",
  "missing:name": "The tenant name is missing. Complete it in tenant settings.",
  "missing:slug": "The tenant identifier is missing. Please contact support.",
  "missing:default_timezone":
    "The tenant time zone is missing. Complete it in tenant settings.",
  "missing:status": "The tenant status is missing. Please contact support.",
  not_acknowledged:
    "An administrator still needs to confirm the tenant profile.",
  // Organization
  no_active_company:
    "No active company is set up. Add or reactivate a company under Organization Structure.",
  no_active_branch:
    "No active branch is set up. Add or reactivate a branch under Organization Structure.",
  branch_missing_active_company_link:
    "An active branch must be linked to an active company. Update your branches in Organization Structure.",
  // Users and roles
  no_owner_admin:
    "No active Owner or Admin user exists. Invite or activate one under User Management.",
  no_manager_trainer:
    "No active Manager or Trainer exists. Invite or activate one under User Management.",
  no_representative:
    "No active Representative exists. Invite or activate one under User Management.",
  // Template
  no_template_applied:
    "A CRM template has not been applied yet. Selection is available in the next step of setup.",
  // Pipeline
  no_default_pipeline:
    "The tenant does not yet have a default pipeline. Apply a CRM template to create one.",
  // Contact import
  contact_entity_unsupported:
    "Contact imports are currently unavailable. Please contact support.",
  csv_extension_unsupported:
    "CSV imports are currently unavailable. Please contact support.",
  field_dictionary_unavailable:
    "The import field dictionary is unavailable. Please try again later.",
  field_dictionary_invalid:
    "The import field dictionary is misconfigured. Please contact support.",
  field_dictionary_missing_required:
    "The import field dictionary is incomplete. Please contact support.",
  field_dictionary_shape_invalid:
    "The import field dictionary is misconfigured. Please contact support.",
  no_authorized_importer:
    "No user with Owner, Admin, Manager, or Trainer permissions is set up to run a Contact import.",
  no_valid_import_scope:
    "No user has a valid tenant-wide, company, branch, or team scope for Contact imports.",
  authorized_user_inactive:
    "The user who would run Contact imports is inactive. Reactivate them or invite another.",
  import_config_missing:
    "Contact import settings are unavailable. Please contact support.",
  // Launch readiness
  no_training_material:
    "Upload at least one approved quick-start or first-login guide in Training before completing onboarding.",
};

const PIPELINE_PREFIXES: Array<[string, string]> = [
  ["missing_canonical_stage:", "The default pipeline is missing a required canonical stage."],
  ["duplicate_canonical_stage:", "The default pipeline has a duplicated canonical stage."],
  ["canonical_stage_order_invalid:", "The default pipeline stages are out of order."],
  ["canonical_stage_order_mismatch:", "The default pipeline stages are out of order."],
  ["canonical_stage_not_required:", "A required canonical stage has been marked optional in the default pipeline."],
  ["canonical_stage_disabled:", "A required canonical stage is disabled in the default pipeline."],
];

const AUTHORIZED_PREFIX = "owner_admin:";
const AUTHORIZED_PREFIX_2 = "manager_trainer:";

const GENERIC = "This item is not yet complete. Refer to the linked setup screen or contact support.";

export function safeReasonForItem(item: OnboardingItem): string | null {
  if (item.status === "complete") return null;
  const raw = item.reason;
  if (!raw) {
    // Automatic + no reason → generic fallback per classification.
    if (item.classification === "acknowledged") {
      return "An administrator still needs to confirm this item.";
    }
    return GENERIC;
  }
  if (REASONS[raw]) return REASONS[raw];
  for (const [prefix, copy] of PIPELINE_PREFIXES) {
    if (raw.startsWith(prefix)) return copy;
  }
  if (raw.startsWith(AUTHORIZED_PREFIX) || raw.startsWith(AUTHORIZED_PREFIX_2)) {
    return "The authorized user for Contact imports has a hierarchy problem. Please contact support.";
  }
  // Do NOT expose the raw reason string — fall back safely.
  return GENERIC;
}

/** Operational link where the reason implies a specific existing screen.
 * Returns null when no safe link applies (e.g. contact-support cases). */
export function linkForItem(item: OnboardingItem): { href: string; label: string } | null {
  switch (item.key) {
    case "company_configured":
    case "branch_configured":
      return { href: "/admin/structure", label: "Open Organization Structure" };
    case "owner_admin_exists":
    case "manager_trainer_exists":
    case "representative_exists":
      return { href: "/admin/users", label: "Open User Management" };
    case "pipeline_stages_ready":
      return { href: "/config", label: "Open Pipeline Settings" };
    case "contact_import_ready":
      return { href: "/imports/new", label: "Open Contact Import" };
    case "training_material_ready":
      return { href: "/training", label: "Open Training" };
    default:
      return null;
  }
}
